import * as React from 'react';
import classNames from 'classnames';
import { ApolloQueryResult } from '@apollo/client/core/types';
import { AllowedSidebarMap, useFetch } from '../../../hooks';
import { AnalyticsEventSource, ScreenEventAnalytics, UNKNOWN_SCREEN } from '@atlassian/temp-analytics';
import { AppContent, AppPage, PageHeader } from '../../../application';
import { ErrorPanel } from '../../../error-page';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { CommerceNoteSearchQuotes } from '../../../../account-subscriptions/cloud-site/src/tabs/commerce-note-search/commerce-note-search';
import {
  getBreadcrumbTrailDefault,
  getHeaderDefault,
  getIconDefault,
  getSubheaderDefault,
  getActionsDefaultQuote,
} from '../billing-doc-utils';
import BillingDocsBreadcrumbs from './billing-doc-breadcrumbs';
import BillingDocDetails from './billing-doc-details';
import './billing-doc-page.scss';
import { SourceSystem } from '@atlassian/cssxp-typescommon';
import { BillingDocWithLineItems, Invoice } from '@atlassian/cssxp-types-billing';
import { useFeatureToggle } from '../../../hooks/src/use-feature-toggle';
import { AuditTrailTabProps } from '../../../audit-notes/src/audit-notes';
import { PaymentRetry } from '../../../payment-retry/index';
import { ENABLE_INVOICE_VIEW_PAYMENT_RETRY } from '@atlassian/cssxp-utils-generic';
import { getAdaBaseURL } from '../../../security/src/user-permission';
import { fetchOrganization } from '@atlassian/temp-proxies';
interface Props {
  analyticsScreenSource?: AnalyticsEventSource;
  additionalDetailsContent?: React.ReactNode;
  getBreadcrumbTrail?: typeof getBreadcrumbTrailDefault;
  headerProps?: {
    getHeader?: typeof getHeaderDefault;
    getSubheader?: typeof getSubheaderDefault;
    getIcon?: typeof getIconDefault;
    getActions?: typeof getActionsDefaultQuote;
    viewers?: [];
  };
  billingDoc?: BillingDocWithLineItems;
  isFetchingBillingDoc: boolean;
  billingDocFetchError: Error | null;
  auditTrailProps?: AuditTrailTabProps;
  BillingDocLineItems?: React.ComponentType<BillingDocWithLineItems>;
  allowedSidebars?: AllowedSidebarMap;
  paymentRetry?: Invoice['dunningStatus'];
  approvalSection?: React.ReactNode;
  onComplete: () => Promise<ApolloQueryResult<unknown>>;
  appPageClass?: string;
}
/**
 * Common Billing Document Page for Invoices, Quotes, Refunds
 */
export const BillingDocPageQuote = ({
  billingDoc,
  isFetchingBillingDoc,
  billingDocFetchError,
  headerProps,
  auditTrailProps,
  analyticsScreenSource,
  additionalDetailsContent,
  BillingDocLineItems,
  getBreadcrumbTrail,
  allowedSidebars,
  paymentRetry,
  approvalSection,
  appPageClass,
}: Props) => {
  const domain = billingDoc?.domain;

  const { data: organization } = useFetch({
    api: () => fetchOrganization(domain ?? ''),
    deps: [domain],
  });

  const [isEnabled] = useFeatureToggle();

  if (isFetchingBillingDoc) {
    return <LoadingIndicator />;
  }

  if (billingDocFetchError) {
    return (
      <div data-testid="error-panel">
        <ErrorPanel error={billingDocFetchError} />
      </div>
    );
  }

  if (!billingDoc) {
    return null;
  }

  const renderHeader = () => {
    const getHeader = headerProps?.getHeader ?? getHeaderDefault;
    const getSubheader = headerProps?.getSubheader ?? getSubheaderDefault;
    const getIcon = headerProps?.getIcon ?? getIconDefault;
    const getActions = headerProps?.getActions ?? getActionsDefaultQuote;
    const adaBaseUrl = getAdaBaseURL();
    return (
      <PageHeader
        icon={getIcon(billingDoc)}
        header={getHeader(billingDoc)}
        subheader={getSubheader(billingDoc)}
        actions={getActions(billingDoc, adaBaseUrl)}
        viewers={headerProps?.viewers}
      />
    );
  };

  const renderBreadcrumbs = () => {
    const breadcrumbTrail = (getBreadcrumbTrail ?? getBreadcrumbTrailDefault)(billingDoc, organization);
    return <BillingDocsBreadcrumbs breadcrumbTrail={breadcrumbTrail} />;
  };

  // eslint-disable-next-line no-unused-vars
  const renderAuditTrail = (_sourceSystem?: SourceSystem | null) => {
    if (auditTrailProps) {
      return (
        //@ts-ignore
        <CommerceNoteSearchQuotes {...auditTrailProps} sourceSystem={_sourceSystem} />
      );
    }
    return null;
  };

  const renderLineItems = (): JSX.Element | undefined => {
    if (BillingDocLineItems) {
      return <BillingDocLineItems {...billingDoc} />;
    }
  };

  const renderApprovalSection = () => {
    return <>{approvalSection}</>;
  };

  return (
    <AppPage className={classNames('billing-document', appPageClass)} allowedSidebars={allowedSidebars}>
      <ScreenEventAnalytics
        event={{
          name: analyticsScreenSource ?? UNKNOWN_SCREEN,
          attributes: {
            id: billingDoc.id,
          },
        }}
      />
      {renderBreadcrumbs()}
      <AppContent className="billing-details">
        <div>{renderHeader()}</div>
        {/* TODO: Need to change further if need HAMS related invoice */}
        {/*{billingDoc.sourceSystem === SourceSystem.HAMS && (
          <>
             Perhaps not pass audit trail, maybe create an entity enum
            <BillingDocMessage entity={'invoice'} adaUrl={auditTrailProps?.adaUrl} />
          </>
        )}*/}

        <BillingDocDetails additionalContent={additionalDetailsContent} billingDoc={billingDoc} />
        {renderLineItems()}
        {renderAuditTrail(billingDoc.sourceSystem)}
        {renderApprovalSection()}
        {paymentRetry && isEnabled(ENABLE_INVOICE_VIEW_PAYMENT_RETRY) && <PaymentRetry dunningInfo={paymentRetry} />}
      </AppContent>
    </AppPage>
  );
};

export default BillingDocPageQuote;
